import React, { useState, useCallback } from 'react';
import { Chip } from '@material-ui/core';
import { SELECTOR_TYPES, DIRECTORY_TYPES, AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import { CUSTOM_INSIGHTS_FIELDS } from '../../common/fields';
import * as actions from '../../containers/CustomInsights/customInsightsActions';

import { ENTITY_TYPES_TO_ICONS } from '../../../Controls/Modals/common/selectorsConsts';
import Button from '../Controls/Button/Button';
import Label from '../Controls/Label/Label';
import TextInput from '../Controls/TextInput/TextInput';
import styles from './CustomInsight.module.css';
import ADEntitiesSelector from '../../../Controls/Modals/ADEntitiesSelector/ADEntitiesSelector';
import ConventionCreator from './ConventionCreator';
import { RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS } from '@silverfort-nodelibs/common/utils/insight';

const {
    NAME,
    INCLUDE_NAMING_CONVENTIONS,
    EXCLUDE_NAMING_CONVENTIONS,
    USERS_AND_GROUPS,
    EXCLUDED_USERS_AND_GROUPS,
    IS_STATIC_INSIGHT,
    INSIGHT_ENTITY_TYPE
} = CUSTOM_INSIGHTS_FIELDS;

const CustomInsight = ({ fields, tabKey, dispatch, onDelete, allowDelete = true }) => {
    const [showEntitiesModal, setShowEntitiesModal] = useState(false);

    let name = fields[NAME.ID];
    const includeNamingConventions = fields[INCLUDE_NAMING_CONVENTIONS.ID];
    const excludeNamingConventions = fields[EXCLUDE_NAMING_CONVENTIONS.ID];
    const usersAndGroups = fields[USERS_AND_GROUPS.ID];
    const excludedUsersAndGroups = fields[EXCLUDED_USERS_AND_GROUPS.ID];
    const entityType = fields[INSIGHT_ENTITY_TYPE.ID];

    let selectorType = SELECTOR_TYPES.ENTITIES;
    if (entityType?.value === AD_ENTITY_TYPES.SERVER) {
        selectorType = SELECTOR_TYPES.SERVERS;
    }

    const staticIndicatorData = RISK_INDICATORS_FIELDS_FOR_CUSTOM_INSIGHTS[name.value];

    if (staticIndicatorData) {
        name = { ...name };
        name.value = staticIndicatorData.NAME;
    }

    const toggleEntitiesModal = useCallback(() => {
        setShowEntitiesModal(!showEntitiesModal);
    }, [showEntitiesModal, setShowEntitiesModal]);

    const dispatchUpdate = useCallback(
        (fieldId, value) => {
            dispatch({ type: actions.UPDATE_CUSTOM_INSIGHT, payload: { tabKey, fieldId, value } });
        },
        [dispatch, tabKey]
    );

    const dispatchAddNamingConvention = useCallback(
        (fieldId) => {
            dispatch({ type: actions.ADD_NAMING_CONVENTION, payload: { tabKey, fieldId } });
        },
        [dispatch, tabKey]
    );

    const dispatchUpdateArray = useCallback(
        (index, fieldId, arrFieldId, value) => {
            dispatch({
                type: actions.UPDATE_CUSTOM_INSIGHT_ARRAY,
                payload: { tabKey, fieldId, index, arrFieldId, value }
            });
        },
        [dispatch, tabKey]
    );

    const dispatchRemoveArrayItem = useCallback(
        (fieldId, index) => {
            dispatch({ type: actions.REMOVE_CUSTOM_INSIGHT_ARRAY_ITEM, payload: { tabKey, fieldId, index } });
        },
        [dispatch, tabKey]
    );

    const onSaveEntitiesClicked = (newEntities) => {
        if (usersAndGroups) {
            dispatchUpdate(
                USERS_AND_GROUPS.ID,
                newEntities.objectEntities.filter((userAndGroup) => !userAndGroup.excluded)
            );
        }
        dispatchUpdate(
            EXCLUDED_USERS_AND_GROUPS.ID,
            newEntities.objectEntities.filter((userAndGroup) => userAndGroup.excluded)
        );
        toggleEntitiesModal();
    };

    const selectedUsersAndGroupsEntities = [];
    if (usersAndGroups) {
        selectedUsersAndGroupsEntities.push(...usersAndGroups.value);
    }

    if (excludedUsersAndGroups) {
        selectedUsersAndGroupsEntities.push(
            ...excludedUsersAndGroups.value.map((excludedUserAndGroup) => ({
                ...excludedUserAndGroup,
                excluded: true
            }))
        );
    }

    const nameField = name && <TextInput field={name} disabled={true} changed={dispatchUpdate} />;
    const entityText = selectorType === SELECTOR_TYPES.SERVERS ? 'servers' : 'users';

    return (
        <>
            {nameField}
            {(includeNamingConventions || excludeNamingConventions) && (
                <>
                    <Label value={INCLUDE_NAMING_CONVENTIONS.NAME} />
                    <div className={styles.conventionContainer}>
                        {includeNamingConventions && (
                            <ConventionCreator
                                key={'INCLUDE'}
                                title="Include"
                                NAMING_CONVENTION_FIELD={INCLUDE_NAMING_CONVENTIONS}
                                namingConventions={includeNamingConventions}
                                addNewNamingConvention={dispatchAddNamingConvention}
                                updateConventionsArray={dispatchUpdateArray}
                                removeItem={dispatchRemoveArrayItem}
                            />
                        )}
                        {excludeNamingConventions && (
                            <ConventionCreator
                                key={'EXCLUDE'}
                                title="Exclude"
                                NAMING_CONVENTION_FIELD={EXCLUDE_NAMING_CONVENTIONS}
                                namingConventions={excludeNamingConventions}
                                addNewNamingConvention={dispatchAddNamingConvention}
                                updateConventionsArray={dispatchUpdateArray}
                                removeItem={dispatchRemoveArrayItem}
                                hideBorder={true}
                            />
                        )}
                    </div>
                </>
            )}
            {(usersAndGroups || excludedUsersAndGroups) && (
                <div className={styles.userAndGroups}>
                    {usersAndGroups && (
                        <>
                            <Label value={USERS_AND_GROUPS.NAME} />
                            <div className={styles.selectedUsersAndGroups}>
                                {usersAndGroups.value.length === 0 && (
                                    <div className={styles.noEntitiesLabel}>No {entityText} or groups selected</div>
                                )}
                                {usersAndGroups.value.map((entity, index) => {
                                    return (
                                        <Chip
                                            key={USERS_AND_GROUPS.ID + index}
                                            id={USERS_AND_GROUPS.ID + index}
                                            variant="outlined"
                                            deleteIcon={<i className="fa fa-times"></i>}
                                            onDelete={() => dispatchRemoveArrayItem(USERS_AND_GROUPS.ID, index)}
                                            label={entity.name}
                                            icon={<img src={ENTITY_TYPES_TO_ICONS[entity.type]} alt="" />}
                                        />
                                    );
                                })}
                            </div>
                            {usersAndGroups.error && <div className={styles.errorText}>{usersAndGroups.error}</div>}
                        </>
                    )}
                    {excludedUsersAndGroups && (
                        <div className={styles.userAndGroups}>
                            <Label value={EXCLUDED_USERS_AND_GROUPS.NAME} />
                            <div className={styles.selectedUsersAndGroups}>
                                {excludedUsersAndGroups.value.length === 0 && (
                                    <div className={styles.noEntitiesLabel}>No {entityText} or groups selected</div>
                                )}
                                {excludedUsersAndGroups.value.map((entity, index) => {
                                    return (
                                        <Chip
                                            key={EXCLUDED_USERS_AND_GROUPS.ID + index}
                                            id={EXCLUDED_USERS_AND_GROUPS.ID + index}
                                            variant="outlined"
                                            deleteIcon={<i className="fa fa-times"></i>}
                                            onDelete={() =>
                                                dispatchRemoveArrayItem(EXCLUDED_USERS_AND_GROUPS.ID, index)
                                            }
                                            label={entity.name}
                                            icon={<img src={ENTITY_TYPES_TO_ICONS[entity.type]} alt="" />}
                                        />
                                    );
                                })}
                            </div>
                            {excludedUsersAndGroups.error && (
                                <div className={styles.errorText}>{excludedUsersAndGroups.error}</div>
                            )}
                        </div>
                    )}
                    <Button style={{ marginTop: '20px' }} onClick={toggleEntitiesModal}>
                        Edit Scope
                    </Button>
                    {showEntitiesModal && (
                        <ADEntitiesSelector
                            selectedObjectEntities={selectedUsersAndGroupsEntities}
                            selectorType={selectorType}
                            showModal
                            handleCloseModal={toggleEntitiesModal}
                            saveEntities={onSaveEntitiesClicked}
                            showExcluded={true}
                            disableAdd={!usersAndGroups}
                            authTypes={[DIRECTORY_TYPES.ad.key]}
                        />
                    )}
                </div>
            )}
            {allowDelete && (
                <Button className="my-5 mx-0" onClick={() => onDelete(tabKey)} deleteButton>
                    Delete
                </Button>
            )}
        </>
    );
};

export default React.memo(CustomInsight);
